import { useState } from 'react'

import { useCreateFloor } from '../hooks/useCreateFloor'
import { useFloorPlanUploadProcessing } from '../hooks/useFloorPlanUploadProcessing'
import { useLightMode } from '@/hooks/useLightMode'
import { usePermissions } from '@/hooks/usePermissions'
import { useToasts } from '@/hooks/useToasts'
import { UPLOAD_ERROR_COMPONENTS, reportUploadError } from '@/hooks/useUpload'
import { SubmitHandler, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { Box, Flex, Modal, Spinner, Tooltip } from '@hivewatch/dark'

import {
  ControlledSelect,
  ControlledTextInput,
  FacilityOptionLabel,
  FormFileUploadControl,
} from '@/components/ui'
import { CreateFloorInput } from '@/graphql/generated/schemas'
import { createFacilityAddressFilter } from '@/utils/forms/optionBuilders'
import { getFacilityNoOptionsMessage } from '@/utils/forms/validation'

import { FloorFormInputsIProps } from '../types/types'
import {
  getErrorMessage,
  getSuccessMessage,
  isValidFileType,
} from '../utils/utils'
import { FloorPlanCropper } from './FloorPlanCropper'
import { PdfPageSelector } from './PdfPageSelector'

interface DarkCreateFloorModalIProps {
  isOpen: boolean
  onClose: () => void
}

/**
 * Rebrand variant of {@link CreateFloorModal} built on `@hivewatch/dark`. The
 * upload pipeline — PDF page selection, cropping and `getFileForUpload` — is
 * reused untouched; `PdfPageSelector` and `FloorPlanCropper` are already
 * theme-aware, and the file field keys itself through `colorScheme`.
 */
export const DarkCreateFloorModal = ({
  isOpen,
  onClose,
}: DarkCreateFloorModalIProps) => {
  const { t } = useTranslation()
  const colorScheme = useLightMode()
  const { shouldEnableCreateFloor } = usePermissions()
  const [isLoading, setIsLoading] = useState(false)
  const [fileTooltipAnchor, setFileTooltipAnchor] =
    useState<HTMLElement | null>(null)
  const { showSuccess, showError } = useToasts()
  const {
    facilityOptions,
    create,
    setFacilityName,
    isFacilitiesLoading,
    isFacilitiesError,
  } = useCreateFloor()
  const { trigger, watch, handleSubmit, register, control, errors } =
    useForm<FloorFormInputsIProps>({
      reValidateMode: 'onChange',
    })
  const fileUpload = watch('file')

  const {
    selectedPdfPage,
    setSelectedPdfPage,
    isPageConfirmed,
    confirmPage,
    unconfirmPage,
    selectedFile,
    isPdf,
    resetPdfPage,
    getFileForUpload,
    pdfSelectorRef,
    crop,
    setCrop,
    imagePreviewUrl,
    isPreviewLoading,
    shouldShowCropper,
    cropperRef,
    resetCrop,
  } = useFloorPlanUploadProcessing({ fileUpload })

  const onSubmit: SubmitHandler<FloorFormInputsIProps> = async (values) => {
    setIsLoading(true)
    const input: CreateFloorInput = {
      name: values.name,
      facilityId: values.facility?.value,
    }

    try {
      const file = await getFileForUpload(values.file?.item(0))
      await create(input, file)
      showSuccess(getSuccessMessage(values.name, 'create', file))
      onClose()
    } catch (e) {
      reportUploadError(e, UPLOAD_ERROR_COMPONENTS.CreateFloorModal)
      showError(getErrorMessage(values.name, 'create', e))
    }
    setIsLoading(false)
  }

  return (
    <Modal
      bodyStyleOverrides={{ minH: 0, overflowY: 'auto' }}
      colorScheme={colorScheme}
      isOpen={isOpen}
      isSaveDisabled={!isValidFileType(fileUpload) || !shouldEnableCreateFloor}
      isSaveLoading={isLoading}
      maxW='600px'
      onClose={onClose}
      onSave={handleSubmit(onSubmit)}
      saveButtonLabel={t('floors:crud_create_button')}
      showCancelButton
      size='compact'
      title={t('floors:crud_create_title')}
    >
      <Flex direction='column' gap='16px'>
        <ControlledTextInput
          colorScheme={colorScheme}
          control={control}
          data-testid='createFloor_modal_floorName'
          disabled={isLoading}
          errors={errors}
          label={t('common:floor_name')}
          name='name'
          placeholder={t('floors:crud_floor_name_placeholder')}
          rules={{ required: t('floors:crud_floor_name_required') }}
          width='100%'
        />

        <ControlledSelect
          colorScheme={colorScheme}
          control={control}
          data-testid='createFloor_modal_facility'
          filterOption={createFacilityAddressFilter}
          formatOptionLabel={(option, meta) => (
            <FacilityOptionLabel
              colorScheme={colorScheme}
              isValueContext={meta?.context === 'value'}
              option={option}
            />
          )}
          isClearable
          isDisabled={isLoading}
          isLoading={isFacilitiesLoading}
          label={t('common:facility')}
          name='facility'
          noOptionsMessage={({ inputValue }) =>
            getFacilityNoOptionsMessage(
              inputValue,
              isFacilitiesError,
              t('common:facility_search_error')
            )
          }
          onInputChange={setFacilityName}
          options={facilityOptions}
          placeholder={t('floors:crud_facility_placeholder')}
          rules={{ required: true }}
        />

        <Box
          data-testid='createFloor_modal_floorplan_hint'
          onMouseEnter={(event) => setFileTooltipAnchor(event.currentTarget)}
          onMouseLeave={() => setFileTooltipAnchor(null)}
        >
          <FormFileUploadControl
            accept='.png,.pdf,.jpg,.jpeg'
            colorScheme={colorScheme}
            dataTestId='createFloor_modal_floorplan'
            errorMessage={
              fileUpload &&
              !isValidFileType(fileUpload) &&
              t('floors:crud_file_error')
            }
            id='file'
            inputRef={register({ required: false })}
            isDisabled={isLoading}
            isInvalid={!isValidFileType(fileUpload)}
            label={t('floors:crud_floor_plan_label')}
            onFileRemoved={() => {
              trigger(['file'])
              resetPdfPage()
              resetCrop()
            }}
            placeholder={t('floors:crud_floor_plan_placeholder')}
          />
        </Box>
        <Tooltip
          anchorEl={fileTooltipAnchor}
          colorScheme={colorScheme}
          label={t('floors:crud_file_type_tooltip')}
          variant='description'
        />

        {isPdf && selectedFile && !isPageConfirmed && (
          <Box ref={pdfSelectorRef}>
            <PdfPageSelector
              isDisabled={isLoading}
              onConfirm={confirmPage}
              onPageChange={setSelectedPdfPage}
              pdfFile={selectedFile}
              selectedPage={selectedPdfPage}
            />
          </Box>
        )}

        {isPreviewLoading && (
          <Flex alignItems='center' justifyContent='center' py='32px'>
            <Spinner
              data-testid='createFloor_modal_preview_spinner'
              size='lg'
            />
          </Flex>
        )}

        {shouldShowCropper && imagePreviewUrl && (
          <Box ref={cropperRef}>
            <FloorPlanCropper
              crop={crop}
              disabled={isLoading}
              imageUrl={imagePreviewUrl}
              onChangePage={isPdf ? unconfirmPage : undefined}
              onCropChange={(_, percentCrop) => setCrop(percentCrop)}
              onReset={resetCrop}
            />
          </Box>
        )}
      </Flex>
    </Modal>
  )
}
